import { createContext, useContext } from "react";
import { createDemoAdapter } from "@/adapters/demoGalleryAdapter";
import { createSupabaseGalleryAdapter } from "@/adapters/supabaseGalleryAdapter";
import { mediaService } from "@/services/mediaService";
import { photoService } from "@/services/photoService";

import { storageService } from "@/services/storageService";
import { voiceMessageService } from "@/services/voiceMessageService";
import { createStorageProvider } from "@/services/storage/createStorageProvider";
import { supabaseAuthService } from "@/services/authService";
import { supabaseSettingsService } from "@/services/settingsService";
import { supabaseDeviceCodeService } from "@/services/deviceCodeService";
import { supabaseAdminOpsService, supabaseEventLookupService, supabaseSocialDataService } from "@/services/domainDataService";
import { supabaseClientService } from "@/services/supabaseClientService";
import { supabaseSeatFinderService } from "@/services/seatFinderService";
import { supabaseAdminDashboardService } from "@/services/adminDashboardService";
import { supabaseGoogleDriveService } from "@/services/googleDriveService";
import { weddingPlannerService } from "@/services/weddingPlannerService";
import type { StorageProviderKey } from "@/services/storage/migrationConfig";

export type AppServices = {
  photoService: typeof photoService;
  voiceMessageService: typeof voiceMessageService;
  storageService: typeof storageService;
  mediaService: typeof mediaService;

  authService: typeof supabaseAuthService;
  settingsService: typeof supabaseSettingsService;
  deviceCodeService: typeof supabaseDeviceCodeService;
  eventLookupService: typeof supabaseEventLookupService;
  socialDataService: typeof supabaseSocialDataService;
  adminOpsService: typeof supabaseAdminOpsService;
  supabaseClientService: typeof supabaseClientService;
  seatFinderService: typeof supabaseSeatFinderService;
  adminDashboardService: typeof supabaseAdminDashboardService;
  googleDriveService: typeof supabaseGoogleDriveService;
  weddingPlannerService: typeof weddingPlannerService;
};

export type ServiceContainer = {
  services: AppServices;
  adapters: {
    supabaseGalleryAdapter: ReturnType<typeof createSupabaseGalleryAdapter>;
    demoGalleryAdapter: ReturnType<typeof createDemoAdapter>;
  };
  providers: {
    activeStorageProvider: StorageProviderKey;
    defaultStorageProvider: ReturnType<typeof createStorageProvider>;
  };
};

export const createServiceContainer = (overrides?: Partial<AppServices>): ServiceContainer => {
  const services: AppServices = {
    photoService: overrides?.photoService ?? photoService,
    voiceMessageService: overrides?.voiceMessageService ?? voiceMessageService,
    storageService: overrides?.storageService ?? storageService,
    mediaService: overrides?.mediaService ?? mediaService,

    authService: overrides?.authService ?? supabaseAuthService,
    settingsService: overrides?.settingsService ?? supabaseSettingsService,
    deviceCodeService: overrides?.deviceCodeService ?? supabaseDeviceCodeService,
    eventLookupService: overrides?.eventLookupService ?? supabaseEventLookupService,
    socialDataService: overrides?.socialDataService ?? supabaseSocialDataService,
    adminOpsService: overrides?.adminOpsService ?? supabaseAdminOpsService,
    supabaseClientService: overrides?.supabaseClientService ?? supabaseClientService,
    seatFinderService: overrides?.seatFinderService ?? supabaseSeatFinderService,
    adminDashboardService: overrides?.adminDashboardService ?? supabaseAdminDashboardService,
    googleDriveService: overrides?.googleDriveService ?? supabaseGoogleDriveService,
    weddingPlannerService: overrides?.weddingPlannerService ?? weddingPlannerService,
  };

  return {
    services,
    adapters: {
      supabaseGalleryAdapter: createSupabaseGalleryAdapter({
        photoService: services.photoService,
        voiceMessageService: services.voiceMessageService,
      }),
      demoGalleryAdapter: createDemoAdapter(),
    },
    providers: {
      activeStorageProvider: "r2" as StorageProviderKey,
      defaultStorageProvider: createStorageProvider(),
    },
  };
};

export const defaultServiceContainer = createServiceContainer();

export const ServiceContainerContext = createContext<ServiceContainer>(defaultServiceContainer);

export const useServiceContainer = () => useContext(ServiceContainerContext);
